import { ComponentType, ComponentInstance } from "../../js/component";

class MainMenu extends ComponentInstance {
  #supportsHover = false;
  #menuGroups = new Map();
  #popups = new Set();
  #focusInitialized = false;
  #crossFadeTimeout;
  #hasActiveLinks = false;

  init() {
    this.#supportsHover = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    this.bindHandlers();
    this.initMenus(this.el);

    if(this.#supportsHover) {
      document.addEventListener("pointerdown", this.onPointerDown, true);
    }

    if (!this.#supportsHover) {
      this.observeHeader();
    }

    // Make first top-level item tabbable
    const first = this.el.querySelector('[role="menuitem"]');
    if (first) {
      first.tabIndex = 0;
    }

    const activeLinks = this.el.querySelectorAll(".is-active");

    this.#hasActiveLinks = activeLinks.length > 0;
    if (this.#hasActiveLinks) {
      let isMenuExpanded = false;
      const isVerticalOrientation = this.getSubmenuOrientation(activeLinks[0]) === 'vertical';
      isMenuExpanded = !isVerticalOrientation;
      this.setMenubarExpanded(isMenuExpanded);
      if(!this.#supportsHover) {
        activeLinks[0].setAttribute('aria-expanded', true);
        this.setMenubarExpanded(true);
      }
    }

    if (!this.#supportsHover) {
      this.baseHeight = 42;
      this.syncMenuHeight();
      this.el.querySelectorAll('[role="menu"]').forEach(menu => {
        menu.setAttribute('aria-orientation', 'horizontal')
      });
    }
  }

  bindHandlers() {
    this.onClick = this.onClick.bind(this);
    this.onHover = this.onHover.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
  }

  initMenus(rootMenu) {
    const menuItems = rootMenu.querySelectorAll('[aria-haspopup="true"]');

    this.#menuGroups.set(rootMenu, Array.from(menuItems));

    menuItems.forEach((item) => {
      item.tabIndex = -1;

      if (this.hasSubmenu(item)) {
        this.#popups.add(item);
      }

      // TODO: keyboard accessibility is buggy. validate this
      // item.addEventListener('keydown', this.onKeydown);
      item.addEventListener("click", this.onClick);

      if (this.#supportsHover) {
        if(this.getLevel(item) !== 0) {
          item.addEventListener("mouseenter", this.onHover);
        }
      }

      const submenu = this.getSubmenu(item);
      if (submenu) {
        this.initMenus(submenu);
      }
    });
  }

  /* ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------ */

  observeHeader() {
    const header = this.el.closest('header');

    header.addEventListener('menubar:toggle', (e) => {
      this.syncMenuHeight();
    })
  }

  hasSubmenu(item) {
    return (
      item.getAttribute("aria-haspopup") === "true" && this.getSubmenu(item)
    );
  }

  getSubmenuOrientation(node) {
    if (this.hasSubmenu(node)) {
      const submenu = this.getSubmenu(node);
      return submenu.getAttribute('aria-orientation');
    }
  }

  getSubmenu(item) {
    return item.nextElementSibling?.getAttribute("role") === "menu"
      ? item.nextElementSibling
      : null;
  }

  isOpen(item) {
    return item.getAttribute("aria-expanded") === "true";
  }

  hasActive(item) {
    return item.dataset.active;
  }

  isAncestorPopup(popup, currentItem) {
    const submenu = this.getSubmenu(popup);
    if (!submenu) return false;
    return submenu.contains(currentItem);
  }

  flickMenubarExpansion(shouldPersist = false) {
    if (!shouldPersist) {
      clearTimeout(this.#crossFadeTimeout);
      this.setMenubarExpanded(false);
      this.#crossFadeTimeout = setTimeout(() => {
        this.setMenubarExpanded(true);
      }, 150);
    } else {
      this.setMenubarExpanded(true);
    }
  }

  open(item, shouldPersist = false) {
    const submenu = this.getSubmenu(item);
    if (!submenu) return;

    if (this.getLevel(item) === 0) {
      if (this.getSubmenuOrientation(item) === 'horizontal') {
        this.flickMenubarExpansion(shouldPersist);
      } else {
        this.setMenubarExpanded(false);
      }
    }

    item.setAttribute("aria-expanded", "true");
    submenu.hidden = false;

    this.syncMenuHeight();
  }

  getLevel(item) {
    return Number(item.dataset.level);
  }

  close(item) {
    const submenu = this.getSubmenu(item);
    if (!submenu) return;

    item.setAttribute("aria-expanded", "false");
    submenu.hidden = true;
    this.syncMenuHeight();
  }

  closeAll(currentItem = null) {
    this.#popups.forEach((popup) => {
      const level = this.getLevel(popup);
      const orientation = this.getSubmenuOrientation(popup);
      // Case 1: Outside click → never close level 0
      if (!currentItem) {
        if (level === 0 && orientation === 'horizontal') {
          // Reopen the active menu
          if(this.hasActive(popup)) {
            this.open(popup, true);
          } else {
            this.close(popup);
            if(!this.#hasActiveLinks) {
              this.setMenubarExpanded(false);
            }
          }
          return;
        }
        this.close(popup);
        return;
      }

      // Case 2: Interaction inside menu → existing rules
      if (
        popup === currentItem ||
        this.isAncestorPopup(popup, currentItem)
      ) {
        return;
      }

      this.close(popup);
    });
  }

  setFocus(menu, item) {
    const items = this.#menuGroups.get(menu);
    items.forEach((el) => {
      el.tabIndex = el === item ? 0 : -1;
    });
    item.focus();
  }

  setMenubarExpanded(value) {
    this.el.setAttribute("data-menubar-item-expanded", value.toString());
  }

  isMenubarExpanded() {
    return this.el.getAttribute("data-menubar-item-expanded") === "true";
  }

  syncMenuHeight() {
    if (this.#supportsHover && !this.baseHeight) {
      return;
    }

    let height = 0;

    const openSubmenus = this.el.querySelectorAll(
      '[role="menu"]:not([hidden])',
    );

    if (openSubmenus.length > 0) {
      openSubmenus.forEach((submenu) => {
        height = Math.max(height, this.getSubmenuHeight(submenu));
      });
    }

    this.el.style.height = `${this.baseHeight + height}px`;
  }

  getSubmenuHeight(submenu) {
    if (!submenu) return 0;

    const wasHidden = submenu.hidden;

    if (wasHidden) {
      submenu.hidden = false;
      submenu.offsetHeight; // force layout
    }

    const height = submenu.getBoundingClientRect().height;
    submenu.hidden = wasHidden;

    return height;
  }

  getParentMenu(item) {
    return item.closest('[role="menu"], [role="menubar"]');
  }

  getMenuItems(menu) {
    return this.#menuGroups.get(menu);
  }

  /* ------------------------------------------------------------
   * EVENT HANDLERS
   * ------------------------------------------------------------ */

  onHover(event) {
    const item = event.currentTarget;
    this.closeAll(item);
    if (this.hasSubmenu(item)) {
      this.open(item);
    }
  }

  onClick(event) {
    const item = event.currentTarget;
    const menu = this.getParentMenu(item);

    // if (!this.#focusInitialized) {
    //   this.setFocus(menu, item);
    //   this.#focusInitialized = true;
    // }

    if (!this.hasSubmenu(item)) {
      this.closeAll();
      return;
    }

    // Desktop hover already opened — allow navigation
    if (this.#supportsHover && this.isOpen(item)) {
      return;
    }

    event.preventDefault();

    if (this.isOpen(item)) {
      this.close(item);
    } else {
      this.closeAll(item);
      this.open(item);
    }
  }

  onKeydown(event) {
    const item = event.currentTarget;
    const key = event.key;
    const menu = this.getParentMenu(item);
    const items = this.getMenuItems(menu);
    const index = items.indexOf(item);

    let handled = true;

    switch (key) {
      case "Enter":
      case " ":
        if (this.hasSubmenu(item)) {
          this.open(item);
          const submenu = this.getSubmenu(item);
          const first = submenu.querySelector('[role="menuitem"]');
          if (first) this.setFocus(submenu, first);
        }
        break;

      case "Escape":
        this.close(item);
        break;

      case "ArrowDown":
        if (items[index + 1]) this.setFocus(menu, items[index + 1]);
        break;

      case "ArrowUp":
        if (items[index - 1]) this.setFocus(menu, items[index - 1]);
        break;

      case "Home":
        this.setFocus(menu, items[0]);
        break;

      case "End":
        this.setFocus(menu, items[items.length - 1]);
        break;

      default:
        handled = false;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onPointerDown(event) {
    if (this.el.contains(event.target)) {
      return;
    }
    this.closeAll();
    this.#focusInitialized = false;
  }
}

new ComponentType(MainMenu, "mainmenu", "nav>[role='menubar']");
